import { observer } from 'mobx-react';
import React, { ReactNode, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, OTPInputImperativeActions } from '@wix/design-system';
import { AppContextProvider } from '../../AppLoader';
import { ButtonWithLoader } from '../../ButtonWithLoader/ButtonWithLoader';
import {
  CodeField,
  CodeFieldModel,
} from '../../EmailCodeDialog/EmailCodeDialog';
import { CONDUCTOR_FT, EXPERIMENTS } from '../../../stores/constants';
import { EnforcementCard, EnforcementInputField } from './EnforcementCard';

interface CodeStepCardProps {
  title: ReactNode;
  subtitle: ReactNode;
  onBack: () => void;
  onVerify: () => void | Promise<unknown>;
  verifyDisabled: boolean;
  field: EnforcementInputField & CodeFieldModel;
  resend?: ReactNode;
  comment?: ReactNode;
  inputDataHook?: string;
  maxLength?: number;
  onlyDigits?: boolean;
  showLoader?: boolean;
  footer?: ReactNode;
  priority?: 'primary' | 'secondary';
}

/**
 * The OTP version of the code step: one slot per digit, verified as soon as the
 * code is complete, so there is no Verify button. A rejected code stays, with its
 * first digit selected to retype.
 */
const OtpCodeStepCard: React.FC<CodeStepCardProps> = observer(
  ({
    title,
    subtitle,
    onBack,
    onVerify,
    field,
    resend,
    comment,
    inputDataHook,
    showLoader,
    footer,
    priority = 'secondary',
  }) => {
    const { t } = useTranslation();
    const {
      rootStore: {
        displayStore: { isMobile },
      },
    } = useContext(AppContextProvider);
    const codeInput = useRef<OTPInputImperativeActions>(null);
    const isSubmitting = useRef(false);

    const verify = async () => {
      // Enter also lands here, so skip while verifying and on a code that isn't
      // ready. Read `field` itself: on the last digit `verifyDisabled` is stale.
      if (
        showLoader ||
        isSubmitting.current ||
        !field.value ||
        !field.isValid
      ) {
        return;
      }
      isSubmitting.current = true;
      try {
        await onVerify();
      } finally {
        isSubmitting.current = false;
      }
      if (!field.isValid) {
        codeInput.current?.focus();
      }
    };

    return (
      <EnforcementCard
        priority={priority}
        title={title}
        subtitle={subtitle}
        showBackButton
        backButtonCallback={onBack}
        secondaryButton={resend}
        footer={footer}
      >
        <Box direction="vertical" width="100%">
          <CodeField
            id="code-step"
            field={field}
            label={t('enter_code.code_input_placeholder')}
            dataHook={inputDataHook}
            autoFocus={!isMobile}
            readOnly={!!showLoader}
            isVerifying={!!showLoader}
            revalidateOnChange
            inputRef={codeInput}
            onSubmit={verify}
          />
          {comment}
        </Box>
      </EnforcementCard>
    );
  },
);

/**
 * The shared wix-2025 "enter your code" screen of the post-login flows: the
 * enforcement SMS, email and authenticator-app code steps and the redesigned Dealer
 * offers' code steps (identical layout — only the store values differ). Purely
 * presentational: it renders the card from props and holds no store state.
 * With `shouldUseOtpInputInCodeSteps` on it renders `OtpCodeStepCard` instead.
 * The Verify loader is driven by the caller's `showLoader` (the store's
 * `verificationState`), matching the legacy VerifyButton: it stays on from the
 * click all the way through the post-success redirect, and only clears when a bad
 * code resets the store to Unverified.
 */
export const CodeStepCard: React.FC<CodeStepCardProps> = observer((props) => {
  const {
    title,
    subtitle,
    onBack,
    onVerify,
    verifyDisabled,
    field,
    resend,
    comment,
    inputDataHook,
    maxLength,
    onlyDigits,
    showLoader,
    footer,
    priority = 'secondary',
  } = props;
  const { t } = useTranslation();
  const {
    rootStore: {
      displayStore: { preset },
      experiments,
    },
  } = useContext(AppContextProvider);

  if (
    experiments.get(EXPERIMENTS.SHOULD_USE_OTP_INPUT_IN_CODE_STEPS) ===
    CONDUCTOR_FT.ENABLED
  ) {
    return <OtpCodeStepCard {...props} />;
  }

  return (
    <EnforcementCard
      priority={priority}
      title={title}
      subtitle={subtitle}
      showBackButton
      backButtonCallback={onBack}
      primaryButton={
        <ButtonWithLoader
          skin={preset.buttonDesign?.skin ?? 'standard'}
          type="submit"
          onClick={onVerify}
          disabled={verifyDisabled}
          showLoader={showLoader}
        >
          {t('verify_button')}
        </ButtonWithLoader>
      }
      secondaryButton={resend}
      footer={footer}
    >
      <Box direction="vertical" width="100%">
        <EnforcementCard.Input
          field={field}
          label={t('enter_code.code_input_placeholder')}
          dataHook={inputDataHook}
          autoFocus
          autocomplete="new-password"
          onEnterPressed={onVerify}
          maxLength={maxLength}
          onlyDigits={onlyDigits}
        />
        {comment}
      </Box>
    </EnforcementCard>
  );
});
